import React, { FC, useState, useMemo } from 'react';
import { Card, CardContent, Grid, Typography, makeStyles, Chip } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import VideoPlayer from '../common/VideoPlayer/VideoPlayer';
import { DRMTYPE } from '../../utils/types';
import { VideoData } from '../../utils/playFactoryApi';

dayjs.extend(relativeTime);

interface VideoPlayerDemoProps {
    videoData: VideoData;
}

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    playerGridItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2)
    },
    infoBox: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2)
    },
    infoItem: {
        marginBottom: theme.spacing(1)
    },
    label: {
        fontWeight: 'bold',
        marginRight: theme.spacing(1)
    },
    endpointSelector: {
        marginBottom: theme.spacing(2)
    },
    chip: {
        marginLeft: theme.spacing(1)
    },
    endpointButtons: {
        display: 'flex',
        gap: theme.spacing(1),
        '& > *': {
            marginRight: theme.spacing(1)
        }
    }
}));

const VideoPlayerDemo: FC<VideoPlayerDemoProps> = ({ videoData }) => {
    const classes = useStyles();
    const [selectedEndpoint, setSelectedEndpoint] = useState(0);

    const drmTypes = Object.values(DRMTYPE);

    const [drm, setDrm] = useState<DRMTYPE>(DRMTYPE.WIDEVINE);

    const endpoint = useMemo(() => {
        return videoData.deliveryEndpoints[selectedEndpoint];
    }, [selectedEndpoint, videoData.deliveryEndpoints])

    const videoSourceUrl = endpoint?.url || '';
    const licenseUrl = videoData.licenseAcquisitionUrl;

    const isExpired = useMemo(() => {
        const now = dayjs();
        const deliveryExpiration = dayjs(videoData.expirations.deliveryEndpoints);
        const licenseExpiration = dayjs(videoData.expirations.licenseAcquisitionUrl);
        return now.isAfter(deliveryExpiration) || now.isAfter(licenseExpiration);
    }, [videoData.expirations]);

    const formatExpiration = (dateString: string) => {
        const date = dayjs(dateString);
        const now = dayjs();

        if (now.isAfter(date)) {
            return `Expired ${date.fromNow()}`;
        }
        return `Expires ${date.fromNow()}`;
    };

    return (
        <div className={classes.root}>
            <Card>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={7} className={classes.playerGridItem}>
                            <div className={classes.endpointSelector}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Select CDN Endpoint:
                                </Typography>
                                <div className={classes.endpointButtons}>
                                    {videoData.deliveryEndpoints.map((endpoint, index) => (
                                        <Chip
                                            key={index}
                                            label={endpoint.cdn}
                                            color={selectedEndpoint === index ? "primary" : "default"}
                                            onClick={() => setSelectedEndpoint(index)}
                                            clickable
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className={classes.endpointSelector}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Select DRM types:
                                </Typography>
                                <div className={classes.endpointButtons}>
                                    {drmTypes.map((drmType, index) => (
                                        <Chip
                                            key={index}
                                            label={drmType.toUpperCase()}
                                            color={drm === drmType ? "primary" : "default"}
                                            onClick={() => setDrm(drmType)}
                                            clickable
                                        />
                                    ))}
                                </div>
                            </div>

                            {videoSourceUrl && licenseUrl ? (
                                <>
                                    <VideoPlayer
                                        autoplay={false}
                                        drmType={drm}
                                        height='400px'
                                        laSource={licenseUrl}
                                        source={videoSourceUrl}
                                        timeShift={false}
                                    />
                                    <Alert severity='info'>
                                        <AlertTitle>DRM Protected Content</AlertTitle>
                                        This video uses DRM protection. A VPN connection may be required to watch the content.
                                    </Alert>
                                </>
                            ):(
                                <Alert severity='error'>
                                    <AlertTitle>Content Unavailable</AlertTitle>
                                    Video source or license url is missing!
                                </Alert>
                            )
                            }
                            {isExpired && (
                                <Alert severity='error'>
                                    <AlertTitle>Content Unavailable</AlertTitle>
                                    The video URLs or License have expired!
                                </Alert>
                            )}
                        </Grid>

                        <Grid item xs={12} lg={5}>
                            <div className={classes.infoBox}>
                                <Typography variant="h6" gutterBottom>
                                    Video Information
                                </Typography>

                                <div className={classes.infoItem}>
                                    <Typography variant="body2">
                                        <span className={classes.label}>Current CDN:</span>
                                        {endpoint?.cdn}
                                    </Typography>
                                </div>

                                <div className={classes.infoItem}>
                                    <Typography variant="body2">
                                        <span className={classes.label}>DRM Type:</span>
                                        {drm.toUpperCase()}
                                    </Typography>
                                </div>

                                <div className={classes.infoItem}>
                                    <Typography variant="body2">
                                        <span className={classes.label}>Available Endpoints:</span>
                                        {videoData.deliveryEndpoints.length}
                                    </Typography>
                                </div>

                                <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                                    Expiration Times:
                                </Typography>

                                <div className={classes.infoItem}>
                                    <Typography variant="body2">
                                        <span className={classes.label}>Delivery Endpoints:</span>
                                        {formatExpiration(videoData.expirations.deliveryEndpoints)}
                                        {dayjs().isAfter(videoData.expirations.deliveryEndpoints) && (
                                            <Chip
                                                label="Expired"
                                                color="secondary"
                                                size="small"
                                                className={classes.chip}
                                            />
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {dayjs(videoData.expirations.deliveryEndpoints).format('YYYY-MM-DD HH:mm:ss')}
                                    </Typography>
                                </div>

                                <div className={classes.infoItem}>
                                    <Typography variant="body2">
                                        <span className={classes.label}>License URL:</span>
                                        {formatExpiration(videoData.expirations.licenseAcquisitionUrl)}
                                        {dayjs().isAfter(videoData.expirations.licenseAcquisitionUrl) && (
                                            <Chip
                                                label="Expired"
                                                color="secondary"
                                                size="small"
                                                className={classes.chip}
                                            />
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {dayjs(videoData.expirations.licenseAcquisitionUrl).format('YYYY-MM-DD HH:mm:ss')}
                                    </Typography>
                                </div>

                                <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                                    Endpoint URLs:
                                </Typography>

                                {videoData.deliveryEndpoints.map((endpoint, index) => (
                                    <div key={index} className={classes.infoItem}>
                                        <Typography variant="body2">
                                            <span className={classes.label}>{endpoint.cdn}:</span>
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{
                                                wordBreak: 'break-all',
                                                display: 'block',
                                                marginTop: 4
                                            }}
                                        >
                                            {endpoint.url}
                                        </Typography>
                                    </div>
                                ))}

                                <div style={{ marginTop: 16 }}>
                                    <Typography variant="caption" color="textSecondary">
                                        <span className={classes.label}>License URL:</span>
                                        <br />
                                        {licenseUrl}
                                    </Typography>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </div>
    );
};

export default VideoPlayerDemo;
